import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { BookOpen, Search, Volume2, BookOpenCheck, Users } from 'lucide-react';
import { surahAPI, recitationAPI } from '../services/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const getFileUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  if (url.startsWith('/uploads/')) {
    return `${BACKEND_URL}/api/files/${url.replace('/uploads/', '')}`;
  }
  return `${BACKEND_URL}${url}`;
};

const SurahList = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [surahs, setSurahs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isReciterDialogOpen, setIsReciterDialogOpen] = useState(false);
  const [selectedSurah, setSelectedSurah] = useState(null);
  const [recitations, setRecitations] = useState([]);
  const [showAutoNextModal, setShowAutoNextModal] = useState(false);
  const [autoNextCountdown, setAutoNextCountdown] = useState(7);
  const [nextSurahIndex, setNextSurahIndex] = useState(null);

  useEffect(() => {
    loadSurahs();
  }, []);

  const loadSurahs = async () => {
    try {
      const response = await surahAPI.getAll();
      setSurahs(response.data);
    } catch (error) {
      console.error('Error loading surahs:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredSurahs = surahs.filter(surah => 
    surah.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    surah.nameArabic.includes(searchTerm)
  );

  // Scroll event listener for auto-next modal
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;
      
      // Check if user scrolled to bottom (within 100px threshold)
      if (scrollTop + clientHeight >= scrollHeight - 100) {
        // Only show modal if there are surahs and we haven't shown it yet
        if (surahs.length > 0 && !showAutoNextModal && filteredSurahs.length > 0) {
          const lastVisibleSurah = filteredSurahs[filteredSurahs.length - 1];
          const lastSurahIndex = surahs.findIndex(s => s.id === lastVisibleSurah.id);
          
          // Check if there's a next surah
          if (lastSurahIndex >= 0 && lastSurahIndex < surahs.length - 1) {
            setNextSurahIndex(lastSurahIndex + 1);
            setShowAutoNextModal(true);
            setAutoNextCountdown(7);
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [surahs, showAutoNextModal, filteredSurahs]);

  const handleSurahClick = (surah) => {
    const identifier = surah.slug || surah.id;
    console.log('[SurahList] Click:', surah.name, '→', identifier);
    navigate(`/?surah=${identifier}`);
  };

  const handleOkuClick = (e, surah) => {
    e.stopPropagation();
    navigate(`/?surah=${surah.slug || surah.id}`);
  };

  const handleDinleClick = async (e, surah) => {
    e.stopPropagation();
    setSelectedSurah(surah);
    
    try {
      const response = await recitationAPI.getBySurah(surah.slug || surah.id);
      setRecitations(response.data || []);
      setIsReciterDialogOpen(true);
    } catch (error) {
      console.error('Error loading recitations:', error);
      setRecitations([]);
      setIsReciterDialogOpen(true);
    }
  };

  // Auto-next modal countdown effect
  useEffect(() => {
    if (showAutoNextModal && autoNextCountdown > 0) {
      const timer = setTimeout(() => {
        setAutoNextCountdown(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (showAutoNextModal && autoNextCountdown === 0) {
      // Navigate to next surah
      const nextSurah = surahs[nextSurahIndex];
      if (nextSurah) {
        navigate(`/?surah=${nextSurah.slug || nextSurah.id}`);
      }
      setShowAutoNextModal(false);
    }
  }, [showAutoNextModal, autoNextCountdown, nextSurahIndex, surahs, navigate]);

  const handleCancelAutoNext = () => {
    setShowAutoNextModal(false);
    setAutoNextCountdown(7);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F6F1] flex items-center justify-center">
        <p className="text-[#8B7355]">Yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F6F1] py-8 pb-32">
      <div className="max-w-5xl mx-auto px-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[#5C4033] mb-2">Sureler</h1>
          <p className="text-[#8B7355]">Kuran-ı Kerim&apos;deki 114 surenin listesi</p>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#8B7355] h-5 w-5" />
            <Input
              type="text"
              placeholder="Sure ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white border-[#D5C4A1] text-[#5C4033] placeholder:text-[#B8A894]"
            />
          </div>
        </div>

        {/* Surah List */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredSurahs.sort((a, b) => a.order - b.order).map((surah) => (
            <Card
              key={surah.id}
              onClick={() => handleSurahClick(surah)}
              className="bg-white border-[#E8DCC8] hover:border-[#D5C4A1] hover:shadow-md cursor-pointer p-5 group"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  {/* Surah Number */}
                  <div className="w-12 h-12 bg-[#F5EFE7] group-hover:bg-[#E8DCC8] border-2 border-[#D5C4A1] rounded-lg flex items-center justify-center">
                    <span className="text-lg font-bold text-[#5C4033]">{surah.order}</span>
                  </div>

                  {/* Surah Info */}
                  <div>
                    <h3 className="text-lg font-semibold text-[#5C4033] group-hover:text-[#8B7355]">
                      {surah.name}
                    </h3>
                    <p className="text-sm text-[#8B7355]">
                      {surah.verses} Ayet • {surah.revelationType}
                    </p>
                  </div>
                </div>

                {/* Arabic Name & Action Buttons */}
                <div className="text-right flex flex-col items-end space-y-2">
                  <p className="text-xl text-[#5C4033] font-semibold" style={{ fontFamily: '"Traditional Arabic", serif' }}>
                    {surah.nameArabic}
                  </p>
                  
                  {/* Action Buttons */}
                  <div className="flex items-center space-x-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => handleOkuClick(e, surah)}
                      className="h-8 px-3 text-xs border-[#8B7355] text-[#8B7355] hover:bg-[#8B7355] hover:text-white"
                    >
                      <BookOpenCheck className="h-3.5 w-3.5 mr-1.5" />
                      Oku
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => handleDinleClick(e, surah)}
                      className="h-8 px-3 text-xs border-[#8B7355] text-[#8B7355] hover:bg-[#8B7355] hover:text-white"
                    >
                      <Volume2 className="h-3.5 w-3.5 mr-1.5" />
                      Dinle
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* No Results */}
        {filteredSurahs.length === 0 && (
          <div className="text-center py-12">
            <BookOpen className="h-16 w-16 text-[#D5C4A1] mx-auto mb-4" />
            <p className="text-[#8B7355]">Aradığınız sure bulunamadı.</p>
          </div>
        )}
      </div>

      {/* Auto-Next Surah Modal */}
      <Dialog open={showAutoNextModal} onOpenChange={setShowAutoNextModal}>
        <DialogContent className="sm:max-w-[400px] bg-[#F9F6F1] border-[#D5C4A1]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#5C4033] text-center">
              Sonraki Sure
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-6 text-center">
            <div className="mb-4">
              <div className="w-20 h-20 mx-auto bg-[#8B7355] rounded-full flex items-center justify-center mb-4">
                <span className="text-3xl font-bold text-white">{autoNextCountdown}</span>
              </div>
              <p className="text-[#5C4033] text-lg mb-2">
                {nextSurahIndex !== null && surahs[nextSurahIndex] ? (
                  <>
                    <span className="font-semibold">{surahs[nextSurahIndex].name}</span> suresine geçiliyor...
                  </>
                ) : (
                  'Yükleniyor...'
                )}
              </p>
              <p className="text-[#8B7355] text-sm">
                {autoNextCountdown} saniye içinde otomatik olarak yönlendirileceksiniz
              </p>
            </div>
          </div>

          <div className="flex justify-center space-x-3">
            <Button
              onClick={handleCancelAutoNext}
              variant="outline"
              className="border-[#8B7355] text-[#8B7355] hover:bg-[#8B7355] hover:text-white"
            >
              İptal
            </Button>
            {nextSurahIndex !== null && surahs[nextSurahIndex] && (
              <Button
                onClick={() => {
                  navigate(`/?surah=${surahs[nextSurahIndex].slug || surahs[nextSurahIndex].id}`);
                  setShowAutoNextModal(false);
                }}
                className="bg-[#8B7355] hover:bg-[#6B5744] text-white"
              >
                Hemen Git
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Reciter Selection Modal */}
      <Dialog open={isReciterDialogOpen} onOpenChange={setIsReciterDialogOpen}>
        <DialogContent className="sm:max-w-[500px] bg-[#F9F6F1] border-[#D5C4A1]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#5C4033]">
              {selectedSurah?.name} - Hafız Seçin
            </DialogTitle>
          </DialogHeader>

          <div className="mt-4 space-y-3 max-h-[400px] overflow-y-auto">
            {recitations.length === 0 ? (
              <div className="text-center py-8">
                <Volume2 className="h-12 w-12 text-[#D5C4A1] mx-auto mb-3" />
                <p className="text-[#8B7355]">Bu sure için henüz hafız kaydedilmemiş.</p>
              </div>
            ) : (
              recitations.map((recitation) => (
                <Card
                  key={recitation.id}
                  onClick={() => {
                    setIsReciterDialogOpen(false);
                    // Navigate with recitation ID to auto-start playback
                    navigate(`/?surah=${selectedSurah.slug || selectedSurah.id}&recitation=${recitation.id}`);
                  }}
                  className="p-4 hover:bg-[#F5EFE7] cursor-pointer border-[#E8DCC8] hover:border-[#8B7355] transition-all"
                >
                  <div className="flex items-center space-x-4">
                    {/* Reciter Image */}
                    <div className="w-12 h-12 rounded-full bg-[#E8DCC8] flex items-center justify-center overflow-hidden flex-shrink-0">
                      {recitation.reciter?.image || recitation.reciter?.imageUrl ? (
                        <img 
                          src={getFileUrl(recitation.reciter?.image || recitation.reciter?.imageUrl)}
                          alt={recitation.reciter?.name || 'Hafız'}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            console.error('[SurahList Image Error]', recitation.reciter?.name, '- Failed to load:', e.target.src);
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = '<svg class="h-6 w-6 text-[#8B7355]" fill="currentColor" viewBox="0 0 24 24"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>';
                          }}
                          onLoad={() => {
                            console.log('[SurahList Image Success]', recitation.reciter?.name, 'loaded');
                          }}
                        />
                      ) : (
                        <Users className="h-6 w-6 text-[#8B7355]" />
                      )}
                    </div>

                    {/* Reciter Info */}
                    <div className="flex-1">
                      <h4 className="font-semibold text-[#5C4033]">
                        {recitation.reciter?.name || 'Bilinmeyen Hafız'}
                      </h4>
                      <p className="text-sm text-[#8B7355]">
                        {recitation.reciter?.country ? `${recitation.reciter.country} • ` : ''}{recitation.duration || 'Süre bilgisi yok'}
                      </p>
                    </div>

                    {/* Play Icon */}
                    <Volume2 className="h-5 w-5 text-[#8B7355]" />
                  </div>
                </Card>
              ))
            )}
          </div>

          <div className="mt-4 flex justify-end">
            <Button
              variant="outline"
              onClick={() => setIsReciterDialogOpen(false)}
              className="border-[#8B7355] text-[#8B7355] hover:bg-[#8B7355] hover:text-white"
            >
              Kapat
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SurahList;